import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Search, Bell, ChevronRight, Star, Sparkles } from 'lucide-react';
import { useModelsStore } from '@/stores/models-store';
import { useUIStore } from '@/stores/ui-store';
import { useAuthStore } from '@/stores/auth-store';
import { useNotificationsStore } from '@/stores/notifications-store';
import ModelCard from '@/components/features/models/ModelCard';
import Avatar from '@/components/ui/Avatar';
import { FeaturedCardSkeleton, ModelCardSkeleton } from '@/components/ui/Skeleton';

export default function Home() {
  const {
    featuredModels,
    models,
    categories,
    isLoadingFeatured,
    isLoadingModels,
    isLoadingCategories,
    fetchFeatured,
    fetchModels,
    fetchCategories,
    selectedCategory,
    setSelectedCategory,
    searchModels,
  } = useModelsStore();

  const { setSelectedModel } = useUIStore();
  const { user } = useAuthStore();
  const { unreadCount, setDrawerOpen } = useNotificationsStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    fetchFeatured();
    fetchModels();
    fetchCategories();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    searchModels(searchQuery);
  };

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const featuredModel = featuredModels[0];
  const trendingModels = models.slice(0, 4);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="h-full overflow-y-auto no-scrollbar pb-32"
    >
      {/* Background gradients */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[400px] h-[400px] rounded-full bg-accent-blue/8 blur-[100px]" />
        <div className="absolute top-1/3 -left-40 w-[300px] h-[300px] rounded-full bg-accent-purple/8 blur-[80px]" />
      </div>

      {/* Safe Area Spacer */}
      <div className="safe-area-top" />

      {/* Header */}
      <div className="relative px-6 pt-4 pb-6 flex justify-between items-center">
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <p className="text-white/50 text-sm font-medium mb-0.5">{getGreeting()}</p>
          <h1 className="text-3xl font-bold tracking-tight">Discover</h1>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15 }}
          className="flex items-center gap-3"
        >
          <button
            onClick={() => setDrawerOpen(true)}
            className="w-11 h-11 rounded-full glass flex items-center justify-center relative touch-feedback hover:bg-white/10 transition-colors"
          >
            <Bell size={20} className="text-white/80" />
            {unreadCount > 0 && (
              <span className="absolute top-2 right-2 min-w-[18px] h-[18px] px-1 bg-accent-blue rounded-full shadow-[0_0_8px_rgba(0,212,255,0.8)] border-2 border-background flex items-center justify-center">
                <span className="text-[10px] font-bold">{unreadCount > 9 ? '9+' : unreadCount}</span>
              </span>
            )}
          </button>
          <Avatar
            src={user?.avatar}
            alt={user?.name || 'Profile'}
            size="sm"
            border={true}
          />
        </motion.div>
      </div>

      {/* Search */}
      <motion.form
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        onSubmit={handleSearch}
        className="relative px-6 mb-8"
      >
        <div
          className={`glass rounded-2xl p-4 flex items-center gap-3 transition-all duration-200 ${
            searchFocused ? 'ring-2 ring-accent-blue/40 bg-white/[0.08]' : ''
          }`}
        >
          <Search size={20} className={`transition-colors ${searchFocused ? 'text-accent-blue' : 'text-white/40'}`} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder="Search models, brands..."
            className="bg-transparent border-none outline-none text-white placeholder:text-white/30 w-full text-sm"
          />
        </div>
      </motion.form>

      {/* Categories */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.25 }}
        className="mb-8 overflow-x-auto no-scrollbar"
      >
        <div className="flex gap-2.5 w-max px-6">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all touch-feedback ${
              !selectedCategory
                ? 'bg-white text-black shadow-[0_0_20px_rgba(255,255,255,0.25)]'
                : 'glass text-white/70 hover:bg-white/10'
            }`}
          >
            All
          </button>
          {isLoadingCategories ? (
            <>
              <div className="skeleton w-20 h-10 rounded-full" />
              <div className="skeleton w-24 h-10 rounded-full" />
              <div className="skeleton w-16 h-10 rounded-full" />
            </>
          ) : (
            categories.map((cat, index) => (
              <motion.button
                key={cat.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + index * 0.05 }}
                onClick={() => setSelectedCategory(cat.slug)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all touch-feedback ${
                  selectedCategory === cat.slug
                    ? 'bg-white text-black shadow-[0_0_20px_rgba(255,255,255,0.25)]'
                    : 'glass text-white/70 hover:bg-white/10'
                }`}
              >
                {cat.name}
              </motion.button>
            ))
          )}
        </div>
      </motion.div>

      {/* Featured */}
      <div className="relative px-6 mb-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35 }}
          className="flex justify-between items-center mb-4"
        >
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-yellow-400" />
            <h2 className="text-xl font-bold">Featured</h2>
          </div>
          <button className="text-accent-blue text-sm font-semibold flex items-center gap-0.5 touch-feedback hover:text-accent-blue/80 transition-colors">
            See all <ChevronRight size={16} />
          </button>
        </motion.div>

        {isLoadingFeatured ? (
          <FeaturedCardSkeleton />
        ) : featuredModel ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedModel(featuredModel)}
            className="relative h-[340px] rounded-[32px] overflow-hidden border border-white/10 cursor-pointer group"
          >
            <img
              src={featuredModel.thumbnailUrl}
              alt={featuredModel.name}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />

            {/* Rating badge */}
            <div className="absolute top-4 right-4 glass-dark rounded-full px-3 py-1.5 flex items-center gap-1.5">
              <Star size={14} className="text-yellow-400 fill-yellow-400" />
              <span className="text-xs font-bold">4.9</span>
            </div>

            {/* Premium badge if applicable */}
            {featuredModel.isPremium && (
              <div className="absolute top-4 left-4 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full px-3 py-1.5 flex items-center gap-1.5">
                <Sparkles size={12} className="text-white" />
                <span className="text-xs font-bold text-white">Premium</span>
              </div>
            )}

            <div className="absolute bottom-0 left-0 right-0 p-6">
              <p className="text-accent-blue text-xs font-bold tracking-wider uppercase mb-1.5">
                {featuredModel.brand?.name}
              </p>
              <h3 className="text-2xl font-bold mb-2">{featuredModel.name}</h3>
              <div className="flex justify-between items-center">
                <p className="text-white/60 text-sm line-clamp-1 max-w-[65%]">
                  {featuredModel.description}
                </p>
                <p className="font-bold text-lg">{formatPrice(featuredModel.price)}</p>
              </div>
            </div>
          </motion.div>
        ) : null}
      </div>

      {/* Trending Grid */}
      <div className="relative px-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45 }}
          className="flex justify-between items-center mb-4"
        >
          <h2 className="text-xl font-bold">Trending Now</h2>
          <button className="text-accent-blue text-sm font-semibold flex items-center gap-0.5 touch-feedback hover:text-accent-blue/80 transition-colors">
            See all <ChevronRight size={16} />
          </button>
        </motion.div>
        <div className="grid grid-cols-2 gap-3">
          {isLoadingModels ? (
            <>
              <ModelCardSkeleton />
              <ModelCardSkeleton />
              <ModelCardSkeleton />
              <ModelCardSkeleton />
            </>
          ) : (
            trendingModels.map((model, index) => (
              <motion.div
                key={model.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.05 }}
              >
                <ModelCard model={model} size="sm" />
              </motion.div>
            ))
          )}
        </div>
      </div>
    </motion.div>
  );
}
